import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { Botao, IconeRevelarSenha, Tooltip } from 'src/componentes'
import { Breadcrumb } from 'src/componentes/breadcrumb'
import { Cabecalho } from 'src/componentes/cabecalho'
import { Cores } from 'src/componentes/styles/cores'
import {
  Tabela,
  TabelaDados,
  TabelaRef,
  TabelaResposta
} from 'src/componentes/tabela'
import {
  ExecutionResponse,
  ServiceHealthState,
  SERVICE_HEALTH_STATE_TEMA
} from 'src/dto'
import { formatadores } from 'src/paginas/aluno/painel-financeiro/pagina-financeiro/formatadores'
import { RotasBackgroundServices } from 'src/rotas'
import { Api } from 'src/servicos'

import { BadgeEstado, MensagemErro } from './componentes'
import {
  BotaoOlho,
  colunaDuracaoStyle,
  colunaIconeStyle,
  colunaNumericaStyle,
  Dados,
  LinkCelula,
  Select,
  SubCelula,
  Toolbar,
  ToolbarAcoes,
  ToolbarTitulo
} from './styles'
import { formatarDuracao, montarUrlCloudWatch, numeroOuTraco } from './utils'

type Periodo = '7' | '1' | '30'

const PERIODO_DIAS: Record<Periodo, number> = { '1': 1, '7': 7, '30': 30 }

const ESTADOS_COM_DICA = [
  ServiceHealthState.Failed,
  ServiceHealthState.Stuck,
  ServiceHealthState.Interrupted,
  ServiceHealthState.Suspect,
  ServiceHealthState.CompletedWithErrors
]

export const PaginaExecucoesBackgroundServices: React.FC = () => {
  const tabelaRef = useRef<TabelaRef>()
  const primeiraRender = useRef(true)
  const history = useHistory()
  const { service } = useParams<{ service: string }>()
  const serviceName = decodeURIComponent(service)
  const [periodo, definirPeriodo] = useState<Periodo>('1')

  // A Tabela já carrega no mount; só recarregamos quando o período mudar.
  useEffect(() => {
    if (primeiraRender.current) {
      primeiraRender.current = false
      return
    }
    tabelaRef.current?.IrParaPagina()
    tabelaRef.current?.CarregarDados()
  }, [periodo])

  const obterDados = async (
    pagina: number,
    tamanhoPagina: number
  ): Promise<TabelaResposta | null> => {
    const { startDate, endDate } = Api.calcularPeriodoUtc(PERIODO_DIAS[periodo])
    const dados = await Api.ObterExecucoesServico(
      serviceName,
      startDate,
      endDate,
      pagina,
      tamanhoPagina
    )

    if (!dados) return null

    return {
      Dados: dados.itens,
      Paginacao: {
        Pagina: pagina,
        TotalRegistros: dados.quantidade
      }
    } as TabelaResposta
  }

  const abrirItens = (execucao: ExecutionResponse) => {
    // Passa o resumo da execução via state p/ o cabeçalho do Nível 3 (evita
    // recarregar; o endpoint /items não retorna o resumo da execução).
    history.push(`${RotasBackgroundServices.Itens}/${execucao.id}`, {
      execucao,
      serviceName
    })
  }

  const renderDicaExecucao = (linha: ExecutionResponse) => {
    if (linha.errorMessage) {
      return <MensagemErro mensagem={linha.errorMessage} tipo="erro" />
    }
    if (linha.state === ServiceHealthState.Suspect) {
      return (
        <SubCelula tipo="alerta">
          └ encontrou {linha.totalFound}, processou {linha.totalProcessed} —
          verifique o log via ExecutionId
        </SubCelula>
      )
    }
    if (linha.state === ServiceHealthState.Stuck) {
      return (
        <SubCelula tipo="alerta">
          └ Running sem fim há mais de 3h — verifique o worker
        </SubCelula>
      )
    }
    if (linha.state === ServiceHealthState.Interrupted) {
      return (
        <SubCelula tipo="alerta">
          └ Encerrada no meio (deploy/reinício do worker) — reprocessa no
          próximo ciclo
        </SubCelula>
      )
    }
    return null
  }

  return (
    <>
      <Cabecalho>
        <Breadcrumb
          titulo={serviceName}
          atalhos={[
            {
              texto: 'Background Services',
              acao: () => history.push(RotasBackgroundServices.Resumo)
            },
            { texto: serviceName }
          ]}
        />
      </Cabecalho>

      <Toolbar>
        <ToolbarTitulo>Histórico de execuções</ToolbarTitulo>
        <ToolbarAcoes>
          <Select
            value={periodo}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              definirPeriodo(e.target.value as Periodo)
            }
          >
            <option value="1">Hoje</option>
            <option value="7">Últimos 7 dias</option>
            <option value="30">Últimos 30 dias</option>
          </Select>
          <Botao
            type="button"
            texto="Voltar"
            tema="Link"
            onClick={() => history.push(RotasBackgroundServices.Resumo)}
          />
        </ToolbarAcoes>
      </Toolbar>

      <Dados>
        <Tabela
          ref={tabelaRef}
          Colunas={[
            {
              Chave: 'startedAt',
              RenderizarCabecalho: () => (
                <div style={{ width: 170 }}>Início</div>
              ),
              Renderizar: (linha: TabelaDados) => {
                const execucao = linha as ExecutionResponse
                const inicio = formatadores.dataHoraLocaleString(
                  execucao.startedAt
                )
                return (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {execucao.hasItems && (
                        <BotaoOlho
                          type="button"
                          onClick={() => abrirItens(execucao)}
                          data-tip="Ver parcelas desta execução"
                          data-for={`exec-itens-${execucao.id}`}
                        >
                          {IconeRevelarSenha}
                          <Tooltip
                            id={`exec-itens-${execucao.id}`}
                            textColor={Cores.PRETO}
                            backgroundColor={Cores.CINZA_1_CLARO}
                            place="right"
                          />
                        </BotaoOlho>
                      )}
                      <span>{inicio}</span>
                    </div>
                    {ESTADOS_COM_DICA.includes(execucao.state) &&
                      renderDicaExecucao(execucao)}
                  </div>
                )
              }
            },
            {
              Chave: 'finishedAt',
              RenderizarCabecalho: () => <div style={{ width: 150 }}>Fim</div>,
              Renderizar: (linha: TabelaDados) => (
                <span>
                  {linha.finishedAt
                    ? formatadores.dataHoraLocaleString(linha.finishedAt)
                    : '—'}
                </span>
              )
            },
            {
              Chave: 'state',
              RenderizarCabecalho: () => (
                <div style={{ width: 160 }}>Estado</div>
              ),
              Renderizar: (linha: TabelaDados) => (
                <BadgeEstado
                  tema={
                    SERVICE_HEALTH_STATE_TEMA[
                      (linha as ExecutionResponse).state
                    ]
                  }
                />
              )
            },
            {
              Chave: 'totalFound',
              Alinhamento: 'center',
              StyleColuna: colunaNumericaStyle,
              RenderizarCabecalho: () => <div>Encontr.</div>,
              Renderizar: (linha: TabelaDados) => (
                <>{numeroOuTraco(linha.totalFound)}</>
              )
            },
            {
              Chave: 'totalProcessed',
              Alinhamento: 'center',
              StyleColuna: colunaNumericaStyle,
              RenderizarCabecalho: () => <div>Proc.</div>,
              Renderizar: (linha: TabelaDados) => (
                <>{numeroOuTraco(linha.totalProcessed)}</>
              )
            },
            {
              Chave: 'totalSucceeded',
              Alinhamento: 'center',
              StyleColuna: colunaIconeStyle,
              RenderizarCabecalho: () => <div>✓</div>,
              Renderizar: (linha: TabelaDados) => (
                <>{numeroOuTraco(linha.totalSucceeded)}</>
              )
            },
            {
              Chave: 'totalFailed',
              Alinhamento: 'center',
              StyleColuna: colunaIconeStyle,
              RenderizarCabecalho: () => <div>✗</div>,
              Renderizar: (linha: TabelaDados) => (
                <>{numeroOuTraco(linha.totalFailed)}</>
              )
            },
            {
              Chave: 'totalSkipped',
              Alinhamento: 'center',
              StyleColuna: colunaIconeStyle,
              RenderizarCabecalho: () => <div>⤼</div>,
              Renderizar: (linha: TabelaDados) => (
                <>{numeroOuTraco(linha.totalSkipped)}</>
              )
            },
            {
              Chave: 'durationMs',
              Alinhamento: 'center',
              StyleColuna: colunaDuracaoStyle,
              RenderizarCabecalho: () => <div>Duração</div>,
              Renderizar: (linha: TabelaDados) => (
                <>{formatarDuracao(linha.durationMs)}</>
              )
            },
            {
              Chave: 'cloudwatch',
              Alinhamento: 'center',
              StyleColuna: { minWidth: 110, whiteSpace: 'nowrap' },
              RenderizarCabecalho: () => <div>CloudWatch</div>,
              Renderizar: (linha: TabelaDados) => {
                const execucao = linha as ExecutionResponse
                return (
                  <LinkCelula
                    onClick={() =>
                      window.open(
                        montarUrlCloudWatch(execucao.id, true),
                        '_blank',
                        'noopener'
                      )
                    }
                    data-tip="Ver logs desta execução no CloudWatch"
                    data-for={`cw-${execucao.id}`}
                  >
                    ver logs →
                    <Tooltip
                      id={`cw-${execucao.id}`}
                      textColor={Cores.PRETO}
                      backgroundColor={Cores.CINZA_1_CLARO}
                      place="left"
                    />
                  </LinkCelula>
                )
              }
            }
          ]}
          ObterDados={obterDados}
          ComPaginacao
          TamanhoPagina={25}
          TextoSemResultados="Nenhuma execução no período."
        />
      </Dados>
    </>
  )
}

export default PaginaExecucoesBackgroundServices
